import { ApiService, type ApiRequestConfig } from "./api.service";
import type { ExportStatus } from "@/components/data-table/use-csv-export";
import {
  normalizeIntakeDefinition,
  normalizeIntakeForm,
  normalizeIntakeForms,
  normalizeIntakeSubmission,
  normalizeIntakeSubmissions,
} from "@/lib/intake/normalize-definition";
import { statusReasonSelectionToBody } from "@/lib/intake/status-reasons";
import type { TRequestImportRequest, TRequestImportResult } from "@/types/intake-import";
import type {
  TConvertPreflight,
  TCreateIntakeSubmission,
  TIntakeDefinition,
  TIntakeDuplicationJob,
  TIntakeForm,
  TIntakeRequestDraft,
  TIntakeRequestTypeOption,
  TIntakeSubmission,
  TIntakeSubmissionActivity,
  TIntakeSubmissionComment,
  TIntakeSubmissionsListQuery,
  TIntakeSubmissionsPage,
  TLinkableProjectsPage,
  TLinkProjectsResult,
  TWorkflowConditionField,
  TWorkflowCurrentStage,
  TWorkflowDecisionResult,
  TWorkflowRenderSurface,
  TWorkflowReturnResult,
  TSaveIntakeRequestDraft,
  TStatusReasonSelection,
} from "@/types/intake";
import type { TLinkedProject } from "@/types/project";
import type { IPriorityBreakdown, TPriorityTier } from "@/types/request-prioritization";
import type { components } from "@/types/api.generated";

function buildSubmissionsQuery(params: TIntakeSubmissionsListQuery): string {
  const search = new URLSearchParams();
  if (params.q) search.set("q", params.q);
  for (const key of [
    "form",
    "program",
    "status",
    "requested_by",
    "owner",
    "team",
    "category",
    "sla_state",
    "priority_tier",
  ] as const) {
    const value = params[key];
    if (value && value.length > 0) search.set(key, value.join(","));
  }
  for (const key of [
    "submitted_after",
    "submitted_before",
    "updated_after",
    "updated_before",
  ] as const) {
    const value = params[key];
    if (value) search.set(key, value);
  }
  if (params.archived) search.set("archived", params.archived);
  if (params.order_by) search.set("order_by", params.order_by);
  if (params.group_by) search.set("group_by", params.group_by);
  if (params.cursor) search.set("cursor", params.cursor);
  if (params.per_page != null) search.set("per_page", String(params.per_page));
  if (params.lean) search.set("lean", "1");
  const str = search.toString();
  return str ? `?${str}` : "";
}

export interface TUpdateIntakeForm {
  definition?: TIntakeDefinition;
  name?: string;
  /** Authored rich-text description (NG-352). The plain `description` is derived
   *  server-side and read-only, so it is deliberately NOT writable here. */
  description_html?: string;
  is_active?: boolean;
  teams?: string[];
  request_type?: string;
  /** Form-level default Request Category — a managed WorkspaceCategoryValue id
   *  (request-scoped), or null to clear. */
  default_request_category_value_id?: string | null;
  default_request_owner_ids?: string[];
  /** Managing Team id (#3144). Autosave routes it into the form's draft layer,
   *  so the live owner (and the RBAC rows derived from it) only moves at
   *  Publish. ``null`` clears a *pending* draft change rather than making the
   *  workflow workspace-managed — the BE reserves that for the live field. */
  managing_team_id?: string | null;
}

/** Presign envelope returned by the intake file-upload endpoint.
 *  Either CloudFront PUT (``upload_data.method === "PUT"``) or an S3
 *  presigned POST (no ``method`` field; ``fields`` carries the form-data
 *  policy). The renderer's onUploadFiles hook branches on the shape. */
export interface TIntakeFileUploadPresign {
  asset_id: string;
  asset_url: string | null;
  upload_data: { url: string; method: "PUT" } | { url: string; fields: Record<string, string> };
}

/**
 * A request form's own Program and Global-field choices (NG-2241), as the backend
 * answers them from the form rather than from the `programs/` and `custom-fields/`
 * libraries, which a Guest cannot read. Typed from the generated contract, so a
 * changed body fails `pnpm check:types` here.
 */
export type TIntakeChoiceOptions = components["schemas"]["IntakeChoiceOptions"];

/** Which choices to ask for: a form being filled in, or a request already made. */
export type TIntakeChoiceSource = { formId: string } | { submissionId: string };

export type TWorkflowOwnerPreview = components["schemas"]["WorkflowOwnerPreview"];

export class IntakeService extends ApiService {
  async getChoiceOptions(
    slug: string,
    source: TIntakeChoiceSource,
    config?: ApiRequestConfig,
  ): Promise<TIntakeChoiceOptions> {
    const base = `/api/workspaces/${slug}/intake`;
    const path =
      "formId" in source
        ? `${base}/forms/${source.formId}/choice-options/`
        : `${base}/submissions/${source.submissionId}/choice-options/`;
    return this.get(path, config);
  }

  /**
   * The FE's definition boundary (#3760). Every response that carries an intake
   * ``definition`` passes through one of these four shims, so a payload whose
   * ``pages`` is missing or malformed is coerced to a valid shape here — once —
   * instead of white-screening (or silently emptying) whichever of the ~16
   * downstream ``definition.pages`` read sites the user happened to reach. Each
   * returns the SAME object when nothing needed repairing, so store identity and
   * consumer memoization are untouched.
   *
   * Whether a repair *alarms* depends on whether an empty definition is
   * legitimate at the entry point — a brand-new form serves ``{}`` by design
   * (`IntakeForm.definition` is `JSONField(default=dict)` and `create` never
   * fills it), so reporting that would flood Sentry on an everyday action. The
   * form shims derive the expectation per form from its publish state; the
   * submission shims hold every present definition to "must exist".
   * See ``lib/intake/normalize-definition.ts``.
   */
  private normalizeForm(slug: string, source: string, form: TIntakeForm): TIntakeForm {
    return normalizeIntakeForm(form, { source, workspaceSlug: slug });
  }

  private normalizeForms(slug: string, source: string, forms: TIntakeForm[]): TIntakeForm[] {
    return normalizeIntakeForms(forms, { source, workspaceSlug: slug });
  }

  private normalizeSubmission(
    slug: string,
    source: string,
    submission: TIntakeSubmission,
  ): TIntakeSubmission {
    return normalizeIntakeSubmission(submission, { source, workspaceSlug: slug });
  }

  private normalizeSubmissions(
    slug: string,
    source: string,
    submissions: TIntakeSubmission[],
  ): TIntakeSubmission[] {
    return normalizeIntakeSubmissions(submissions, { source, workspaceSlug: slug });
  }

  /** ``includeDraft`` opts into the builder-only draft overlay (same flag as
   *  ``listForms``). The builder passes it so a deep-linked / refreshed form
   *  reads back its autosaved draft rather than the LIVE shape; requester-side
   *  callers omit it so the draft never leaks. */
  async getForm(
    slug: string,
    formId: string,
    options: { includeDraft?: boolean } = {},
    config?: ApiRequestConfig,
  ): Promise<TIntakeForm> {
    const qs = options.includeDraft ? "?include_draft=true" : "";
    const form = await this.get<TIntakeForm>(
      `/api/workspaces/${slug}/intake/forms/${formId}/${qs}`,
      config,
    );
    return this.normalizeForm(slug, "getForm", form);
  }

  /** ``includeDraft`` flips the builder-only overlay on: top-level
   *  editorial fields reflect the draft layer when populated so the
   *  builder reads back what it just autosaved. Default (false) returns
   *  the LIVE shape, which is what every requester-facing caller wants —
   *  the draft never leaks via the default path. */
  async listForms(
    slug: string,
    options: { includeDraft?: boolean } = {},
    config?: ApiRequestConfig,
  ): Promise<TIntakeForm[]> {
    const qs = options.includeDraft ? "?include_draft=true" : "";
    const forms = await this.get<TIntakeForm[]>(
      `/api/workspaces/${slug}/intake/forms/${qs}`,
      config,
    );
    return this.normalizeForms(slug, "listForms", forms);
  }

  /** The definition the importer must map columns against (NG-963): the **same**
   *  reconstruction the import validates rows with — `resolve_read_definition`
   *  with bank choices hydrated — not the legacy blob that the form list/detail
   *  serializers serve. A published, workflow-backed form's list payload carries
   *  the old blob (mismatched field names, or an empty skeleton for a
   *  builder-authored form), so the importer must fetch this before building its
   *  column map or every row fails backend validation. */
  async getImportDefinition(slug: string, formId: string): Promise<TIntakeForm["definition"]> {
    const { definition } = await this.get<{ definition: TIntakeForm["definition"] }>(
      `/api/workspaces/${slug}/intake/forms/${formId}/import-definition/`,
    );
    return definition;
  }

  /** Bulk-import requests into a form from pre-parsed CSV rows (NG-784). Returns
   *  the per-row report (partial success — some rows create, some come back as
   *  errors). The backend re-validates and resolves every reference, so this is
   *  authoritative over the wizard's client-side hints. */
  async importForm(
    slug: string,
    formId: string,
    body: TRequestImportRequest,
  ): Promise<TRequestImportResult> {
    return this.post<TRequestImportResult>(
      `/api/workspaces/${slug}/intake/forms/${formId}/import/`,
      body,
    );
  }

  /**
   * Paginated + searchable form list. Sends ``per_page`` / ``cursor`` / ``q``
   * so the backend returns its standard paginator envelope instead of the
   * legacy flat array. Used by the requester-side "create request" sidebar
   * (live shape) and the author-facing settings list (``includeDraft: true``
   * so an unpublished workflow shows the name the author just autosaved
   * instead of the default "Untitled form" live value — #2656).
   */
  async listFormsPage(
    slug: string,
    options: {
      cursor?: string | null;
      q?: string;
      per_page?: number;
      active_only?: boolean;
      is_active?: boolean;
      order_by?: string;
      includeDraft?: boolean;
      /** Team-settings Intake Workflows tab (#3144): narrows the page to the
       *  workflows this team or one of its ancestors manages, and is what makes the
       *  server evaluate each row's `can_edit` (it stays `null` without it). */
      team?: string;
    } = {},
    config?: ApiRequestConfig,
  ): Promise<{
    results: TIntakeForm[];
    next_cursor: string;
    prev_cursor: string;
    next_page_results: boolean;
    prev_page_results: boolean;
    total_count: number;
    count: number;
  }> {
    const params = new URLSearchParams();
    params.set("per_page", String(options.per_page ?? 20));
    if (options.cursor) params.set("cursor", options.cursor);
    if (options.q) params.set("q", options.q);
    if (options.active_only) params.set("active_only", "true");
    if (options.is_active != null) params.set("is_active", String(options.is_active));
    if (options.order_by) params.set("order_by", options.order_by);
    if (options.includeDraft) params.set("include_draft", "true");
    if (options.team) params.set("team", options.team);
    const page = await this.get<{
      results: TIntakeForm[];
      next_cursor: string;
      prev_cursor: string;
      next_page_results: boolean;
      prev_page_results: boolean;
      total_count: number;
      count: number;
    }>(`/api/workspaces/${slug}/intake/forms/?${params.toString()}`, config);
    return { ...page, results: this.normalizeForms(slug, "listFormsPage", page.results) };
  }

  async createForm(
    slug: string,
    data: {
      name?: string;
      description_html?: string;
      is_active?: boolean;
      teams?: string[];
      request_type?: string;
      default_request_owner_ids?: string[];
      /** Seeds the new workflow's owner (#3144) — the create path writes the
       *  LIVE field, so a workflow is owned from birth rather than only once
       *  its first draft is published. */
      managing_team_id?: string | null;
    },
  ): Promise<TIntakeForm> {
    const form = await this.post<TIntakeForm>(`/api/workspaces/${slug}/intake/forms/`, data);
    return this.normalizeForm(slug, "createForm", form);
  }

  async listRequestTypes(slug: string): Promise<TIntakeRequestTypeOption[]> {
    return this.get(`/api/workspaces/${slug}/intake/request-types/`);
  }

  async updateForm(slug: string, formId: string, data: TUpdateIntakeForm): Promise<TIntakeForm> {
    const form = await this.patch<TIntakeForm>(
      `/api/workspaces/${slug}/intake/forms/${formId}/`,
      data,
    );
    return this.normalizeForm(slug, "updateForm", form);
  }

  /** Copy the form's autosaved draft fields into the live record. Rejects
   *  with 409 if the form is not Active, or 422 with field-keyed errors if
   *  required Form Details are blank or the definition fails schema. */
  async publishForm(slug: string, formId: string): Promise<TIntakeForm> {
    const form = await this.post<TIntakeForm>(
      `/api/workspaces/${slug}/intake/forms/${formId}/publish/`,
      {},
    );
    return this.normalizeForm(slug, "publishForm", form);
  }

  /** Wipe every draft_* field on the form so the builder reverts to the
   *  last published version. Live fields are untouched. */
  async discardFormDraft(slug: string, formId: string): Promise<TIntakeForm> {
    const form = await this.post<TIntakeForm>(
      `/api/workspaces/${slug}/intake/forms/${formId}/discard-draft/`,
      {},
    );
    return this.normalizeForm(slug, "discardFormDraft", form);
  }

  async deleteForm(slug: string, formId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/intake/forms/${formId}/`);
  }

  /** Enqueue an async deep-copy of a form + its workflow graph into a new,
   *  inactive & unpublished form named "<name> (copy)". Returns a job handle
   *  (202) to poll via `getDuplicationStatus` — cloning a large workflow graph
   *  can exceed the synchronous write budget, so it runs on a worker (#3621).
   *
   *  `targetTeamId` is the team-settings tab's own team (#3144): duplicating from a
   *  team tab means "copy this into THIS team", which the server otherwise has to
   *  guess from the caller's team bindings — and cannot, for a lead of more than one
   *  team. Omitted from the workspace Settings list, which has no team context; the
   *  server then falls back to the caller's sole led team, else the source's teams. */
  async duplicateForm(
    slug: string,
    formId: string,
    targetTeamId?: string,
  ): Promise<TIntakeDuplicationJob> {
    return this.post(
      `/api/workspaces/${slug}/intake/forms/${formId}/duplicate/`,
      targetTeamId ? { target_team_id: targetTeamId } : {},
    );
  }

  /** Poll a duplication job by id. `form_id` is set once `status` is
   *  "succeeded"; `error` is set once "failed". */
  async getDuplicationStatus(slug: string, jobId: string): Promise<TIntakeDuplicationJob> {
    return this.get(`/api/workspaces/${slug}/intake/form-duplications/${jobId}/`);
  }

  /**
   * Bank fields a workflow element may use as condition sources (M4 #2094) —
   * the fields on Pages that unconditionally precede ``elementId`` on the graph
   * (the BE computes precedence). Backs a Branch's condition field picker.
   * Returns ``[]`` for a form with no workflow yet; 404 if the element is
   * unknown.
   */
  async getWorkflowElementAvailableFields(
    slug: string,
    formId: string,
    elementId: string,
  ): Promise<TWorkflowConditionField[]> {
    const { fields } = await this.get<{ fields: TWorkflowConditionField[] }>(
      `/api/workspaces/${slug}/intake/forms/${formId}/workflow/elements/${elementId}/available-fields/`,
    );
    return fields;
  }

  async submitForm(
    slug: string,
    formId: string,
    data: TCreateIntakeSubmission,
  ): Promise<TIntakeSubmission> {
    const submission = await this.post<TIntakeSubmission>(
      `/api/workspaces/${slug}/intake/forms/${formId}/submissions/`,
      data,
    );
    return this.normalizeSubmission(slug, "submitForm", submission);
  }

  /**
   * The workflow-path requester render surface (#2505 / #2524): the fixed first
   * Page served straight from the form's published workflow snapshot. The FE
   * tries this over the legacy form-detail definition for every form (#2503).
   * Self-guards on the backend with 404 when the form has no usable published
   * workflow, so a caller can fall back to the legacy definition on 404. Carries
   * a legacy ``definition`` the existing ``IntakeRenderer`` consumes directly.
   */
  async renderWorkflowSurface(
    slug: string,
    formId: string,
    config?: ApiRequestConfig,
  ): Promise<TWorkflowRenderSurface> {
    const surface = await this.get<TWorkflowRenderSurface>(
      `/api/workspaces/${slug}/intake/forms/${formId}/workflow/render/`,
      config,
    );
    return {
      ...surface,
      definition: normalizeIntakeDefinition(surface.definition, {
        source: "renderWorkflowSurface",
        // This endpoint 404s unless the form HAS a published workflow, so by the
        // time it answers 200 a definition must exist. An empty one here is the
        // incident shape: a published snapshot that failed to reconstruct (#3634).
        expectation: "requires-definition",
        workspaceSlug: slug,
        formId,
      }),
    };
  }

  /**
   * Create a request through the dedicated workflow submit endpoint (#2504 /
   * #2524): creates the submission AND binds its FormWorkflowRun in one call.
   * The FE routes here (instead of ``submitForm``) at request creation when the
   * form renders from its published workflow; once bound, the backend drives the
   * run by state (#2503). Returns the same submission shape as the legacy submit.
   */
  async submitWorkflowForm(
    slug: string,
    formId: string,
    data: TCreateIntakeSubmission,
  ): Promise<TIntakeSubmission> {
    const submission = await this.post<TIntakeSubmission>(
      `/api/workspaces/${slug}/intake/forms/${formId}/workflow-submissions/`,
      data,
    );
    return this.normalizeSubmission(slug, "submitWorkflowForm", submission);
  }

  /** Writes nothing; the answers may be incomplete. */
  async previewWorkflowOwners(
    slug: string,
    formId: string,
    data: Pick<TCreateIntakeSubmission, "response_data" | "completes_requester_pages">,
    config?: { signal?: AbortSignal },
  ): Promise<TWorkflowOwnerPreview> {
    return this.post<TWorkflowOwnerPreview>(
      `/api/workspaces/${slug}/intake/forms/${formId}/workflow/owner-preview/`,
      data,
      config,
    );
  }

  /**
   * The caller's OWN private request drafts (NG-702), newest-edited first.
   * Pass `formId` to scope to one form (the resume-prompt lookup). Privacy is
   * server-enforced — the endpoint never returns anyone else's drafts.
   */
  async listRequestDrafts(
    slug: string,
    formId?: string,
    config?: ApiRequestConfig,
  ): Promise<TIntakeRequestDraft[]> {
    const query = formId ? `?form=${encodeURIComponent(formId)}` : "";
    return this.get(`/api/workspaces/${slug}/intake/request-drafts/${query}`, config);
  }

  /** Save a new private draft of an in-progress fill against `formId`. */
  async createRequestDraft(
    slug: string,
    formId: string,
    data: TSaveIntakeRequestDraft,
  ): Promise<TIntakeRequestDraft> {
    return this.post(`/api/workspaces/${slug}/intake/forms/${formId}/request-drafts/`, data);
  }

  /** One owned draft — 404s for anyone but its creator. */
  async getRequestDraft(slug: string, draftId: string): Promise<TIntakeRequestDraft> {
    return this.get(`/api/workspaces/${slug}/intake/request-drafts/${draftId}/`);
  }

  /** Autosave-friendly partial update of an owned draft. */
  async updateRequestDraft(
    slug: string,
    draftId: string,
    data: TSaveIntakeRequestDraft,
  ): Promise<TIntakeRequestDraft> {
    return this.patch(`/api/workspaces/${slug}/intake/request-drafts/${draftId}/`, data);
  }

  /** Discard an owned draft. */
  async deleteRequestDraft(slug: string, draftId: string): Promise<void> {
    await this.delete(`/api/workspaces/${slug}/intake/request-drafts/${draftId}/`);
  }

  /**
   * Submit an owned draft as a real request (NG-702). The backend materializes
   * it through the same door a direct submit takes (workflow when the form has
   * a usable published workflow, else legacy) and consumes the draft row
   * atomically. The body carries the FINAL fill state; absent, the draft's
   * stored answers are submitted. Full submit validation applies either way.
   */
  async submitRequestDraft(
    slug: string,
    draftId: string,
    data: TCreateIntakeSubmission,
  ): Promise<TIntakeSubmission> {
    const submission = await this.post<TIntakeSubmission>(
      `/api/workspaces/${slug}/intake/request-drafts/${draftId}/submit/`,
      data,
    );
    return this.normalizeSubmission(slug, "submitRequestDraft", submission);
  }

  /**
   * Presign a file upload against a ``kind: file`` element on this form.
   * Server validates the requested ``name`` + ``type`` + ``size`` against
   * the element's per-field config (``max_size_mb`` + ``accepted_mime_types``)
   * and returns the credentials the FE uses to POST/PUT directly to storage.
   */
  async presignIntakeFileUpload(
    slug: string,
    formId: string,
    payload: { field_name: string; name: string; type: string; size: number },
  ): Promise<TIntakeFileUploadPresign> {
    return this.post(`/api/workspaces/${slug}/intake/forms/${formId}/file-uploads/`, payload);
  }

  /**
   * Mark the storage-side upload complete and let the server HEAD the
   * object for real size + metadata. The ``field_name`` is optional but
   * recommended so the server can re-check the element's size cap with
   * the storage-reported size as defense-in-depth against a lying client.
   */
  async finalizeIntakeFileUpload(
    slug: string,
    formId: string,
    assetId: string,
    payload: { field_name?: string; attributes?: Record<string, unknown> } = {},
  ): Promise<{ asset_id: string; asset_url: string | null }> {
    return this.post(
      `/api/workspaces/${slug}/intake/forms/${formId}/file-uploads/${assetId}/finalize/`,
      payload,
    );
  }

  async listSubmissions(
    slug: string,
    params: TIntakeSubmissionsListQuery = {},
    config?: ApiRequestConfig,
  ): Promise<TIntakeSubmissionsPage> {
    const page = await this.get<TIntakeSubmissionsPage>(
      `/api/workspaces/${slug}/intake/submissions/${buildSubmissionsQuery(params)}`,
      config,
    );
    return { ...page, results: this.normalizeSubmissions(slug, "listSubmissions", page.results) };
  }

  /**
   * The in-flight workflow requests awaiting the current user's review — the
   * Form Workflow runtime "awaiting my review" queue (epic #2166, BE #2170).
   * The endpoint already scopes to runs paused on a Review that lists the caller
   * as a reviewer, so no client-side filter is needed. Additive on the backend:
   * it returns an empty list unless the runtime is live, so this is always safe
   * to call.
   */
  async listPendingReviews(
    slug: string,
    config?: ApiRequestConfig,
  ): Promise<{ requests: TIntakeSubmission[] }> {
    const { requests } = await this.get<{ requests: TIntakeSubmission[] }>(
      `/api/workspaces/${slug}/intake/reviews/pending/`,
      config,
    );
    return { requests: this.normalizeSubmissions(slug, "listPendingReviews", requests) };
  }

  /**
   * The render payload for a workflow request's current stage (Form Workflow
   * runtime, epic #2166; BE #2169 + #2254). On a Review it carries
   * `current_review` (its decision options / notification message / reviewers)
   * plus `can_decide`, so the reviewer decision surface renders off one call.
   * Flag-gated on the backend: 404s when the runtime is off or the request has
   * no run (a legacy request).
   */
  async getWorkflowCurrentStage(
    slug: string,
    submissionId: string,
    config?: ApiRequestConfig,
  ): Promise<TWorkflowCurrentStage> {
    return this.get(
      `/api/workspaces/${slug}/intake/submissions/${submissionId}/workflow/current-page/`,
      config,
    );
  }

  /**
   * Submit the Page a request is currently waiting on (BE #2169), advancing the
   * run to whatever follows it. Returns the **next** stage, so a workflow whose
   * lane holds several Pages walks forward without a second read.
   *
   * ``field_values`` is keyed the way the renderer names its questions (the
   * snapshot element id), which is what the BE persists under.
   */
  async submitWorkflowPage(
    slug: string,
    submissionId: string,
    fieldValues: Record<string, unknown>,
  ): Promise<TWorkflowCurrentStage> {
    return this.post(
      `/api/workspaces/${slug}/intake/submissions/${submissionId}/workflow/current-page/submit/`,
      { field_values: fieldValues },
    );
  }

  /**
   * Explicitly mark the Page a request is waiting on as complete and advance the
   * run (NG-703). Unlike {@link submitWorkflowPage} it carries no field values —
   * since NG-2537 the backend instead validates the page's visible required
   * fields against the run's saved values and answers 400 with
   * ``{"field_values": [...]}`` when any are blank (reversing NG-703's "blank
   * means not relevant" rule). Returns the **next** stage, like the submit.
   */
  async completeWorkflowStep(slug: string, submissionId: string): Promise<TWorkflowCurrentStage> {
    return this.post(
      `/api/workspaces/${slug}/intake/submissions/${submissionId}/workflow/current-page/complete/`,
      {},
    );
  }

  /**
   * Record a reviewer's decision on a request's current Review (BE #2170):
   * applies the chosen option's status and routes the request down its lane.
   * First responder wins — a decision racing another reviewer's returns 409.
   */
  async submitWorkflowDecision(
    slug: string,
    submissionId: string,
    decisionOptionId: string,
    reason?: TStatusReasonSelection,
  ): Promise<TWorkflowDecisionResult> {
    const result = await this.post<TWorkflowDecisionResult>(
      `/api/workspaces/${slug}/intake/submissions/${submissionId}/workflow/decision/`,
      {
        decision_option_id: decisionOptionId,
        // Only sent for a reason-gated disposition; omitting them leaves a plain
        // Accept's payload exactly as it was before #4173/#4286.
        ...statusReasonSelectionToBody(reason),
      },
    );
    return {
      ...result,
      request: this.normalizeSubmission(slug, "submitWorkflowDecision", result.request),
    };
  }

  /**
   * Send a request paused on a Review back to its previous step (BE #2254):
   * reverts the run's position + status and notifies the owner. A reviewer may
   * always return a paused request — no body required.
   *
   * Reached only through ``intakeStore.returnWorkflowReview``, which no UI calls
   * today — see the note there before concluding this is dead code.
   */
  async returnWorkflowToPreviousStep(
    slug: string,
    submissionId: string,
  ): Promise<TWorkflowReturnResult> {
    const result = await this.post<TWorkflowReturnResult>(
      `/api/workspaces/${slug}/intake/submissions/${submissionId}/workflow/return/`,
    );
    return {
      ...result,
      request: this.normalizeSubmission(slug, "returnWorkflowToPreviousStep", result.request),
    };
  }

  /**
   * Export the submissions list as CSV, scoped to the same filters/sort as the
   * list (`params`) and, when `body.ids` is set, restricted to those rows.
   * Returns the raw Response so the caller can stream the CSV (200) or poll the
   * async token (202).
   */
  async exportSubmissions(
    slug: string,
    body: { ids?: string[]; columns?: string[] },
    params: TIntakeSubmissionsListQuery = {},
  ): Promise<Response> {
    return this.postRaw(
      `/api/workspaces/${slug}/export-requests/${buildSubmissionsQuery(params)}`,
      body,
    );
  }

  async getExportStatus(slug: string, token: string): Promise<ExportStatus> {
    return this.get(`/api/workspaces/${slug}/export-status/${token}/`);
  }

  /**
   * Export the Forms settings list as CSV, scoped to the same filters/sort as
   * the list (`options`) and, when `body.ids` is set, restricted to those rows.
   * Returns the raw Response so the caller can stream the CSV (200) or poll the
   * async token (202).
   */
  async exportForms(
    slug: string,
    body: { ids?: string[]; columns?: string[] },
    options: { q?: string; order_by?: string; is_active?: boolean; team?: string } = {},
  ): Promise<Response> {
    const params = new URLSearchParams();
    if (options.q) params.set("q", options.q);
    if (options.order_by) params.set("order_by", options.order_by);
    if (options.is_active != null) params.set("is_active", String(options.is_active));
    // Same narrowing as the list (#3144): exporting from a team's tab must produce
    // that tab's rows, not the whole workspace's. The export endpoint forwards every
    // query param to the shared `build_forms_queryset`, so this is the same filter.
    if (options.team) params.set("team", options.team);
    const qs = params.toString();
    return this.postRaw(`/api/workspaces/${slug}/export-forms/${qs ? `?${qs}` : ""}`, body);
  }

  async getSubmission(
    slug: string,
    submissionId: string,
    config?: ApiRequestConfig,
  ): Promise<TIntakeSubmission> {
    const submission = await this.get<TIntakeSubmission>(
      `/api/workspaces/${slug}/intake/submissions/${submissionId}/`,
      config,
    );
    return this.normalizeSubmission(slug, "getSubmission", submission);
  }

  /**
   * The submission's render definition, served from the run's pinned snapshot
   * (#3160). Decoupled from the detail GET's ``form_detail.definition``, which
   * flows through the legacy blob route that falls back to an empty blob when
   * the snapshot fails the strict validity gate (e.g. duplicate option UUIDs) —
   * the source of the prod request-detail crash. This route reconstructs
   * directly, so it renders the actual fields.
   */
  async getSubmissionDefinition(
    slug: string,
    submissionId: string,
    config?: ApiRequestConfig,
  ): Promise<{ definition: TIntakeDefinition }> {
    const { definition } = await this.get<{ definition: TIntakeDefinition }>(
      `/api/v2/workspaces/${slug}/intake/submissions/${submissionId}/definition/`,
      config,
    );
    return {
      definition: normalizeIntakeDefinition(definition, {
        source: "getSubmissionDefinition",
        // The request-render path: an existing request was filled in against a
        // real definition, so an empty one means it was lost.
        expectation: "requires-definition",
        workspaceSlug: slug,
      }),
    };
  }

  /**
   * Plan the request→project field carryover (PRD §5.2 / §5.3).
   *
   * Returns three buckets — shared (auto-carry), extras (user picks), and
   * archived_blocked (warning panel). When ``templateId`` is omitted the
   * shared bucket comes back empty (no template to compare against), so the
   * modal should always pass the currently-selected template.
   */
  async convertPreflight(
    slug: string,
    submissionId: string,
    templateId: string | null,
  ): Promise<TConvertPreflight> {
    const qs = templateId ? `?template_id=${encodeURIComponent(templateId)}` : "";
    return this.get(
      `/api/workspaces/${slug}/intake/submissions/${submissionId}/convert/preflight/${qs}`,
    );
  }

  async updateSubmissionStatus(
    slug: string,
    submissionId: string,
    stateId: string,
    reason?: TStatusReasonSelection,
  ): Promise<TIntakeSubmission> {
    const submission = await this.patch<TIntakeSubmission>(
      `/api/workspaces/${slug}/intake/submissions/${submissionId}/`,
      {
        status: stateId,
        ...statusReasonSelectionToBody(reason),
      },
    );
    return this.normalizeSubmission(slug, "updateSubmissionStatus", submission);
  }

  /** Move a submission to another Home Team (NG-2971); the PATCH gates it on triage rights. */
  async moveSubmissionHomeTeam(
    slug: string,
    submissionId: string,
    teamId: string,
  ): Promise<TIntakeSubmission> {
    const submission = await this.patch<TIntakeSubmission>(
      `/api/workspaces/${slug}/intake/submissions/${submissionId}/`,
      { home_team_id: teamId },
    );
    return this.normalizeSubmission(slug, "moveSubmissionHomeTeam", submission);
  }

  /** Assign the request owners on a submission (writable `default_request_owner_ids`). */
  async updateSubmissionOwners(
    slug: string,
    submissionId: string,
    ownerIds: string[],
  ): Promise<TIntakeSubmission> {
    const submission = await this.patch<TIntakeSubmission>(
      `/api/workspaces/${slug}/intake/submissions/${submissionId}/`,
      { default_request_owner_ids: ownerIds },
    );
    return this.normalizeSubmission(slug, "updateSubmissionOwners", submission);
  }

  /**
   * Partial-update the response_data JSON dict on an IntakeSubmission.
   *
   * The BE shallow-merges the partial dict into the stored ``response_data``
   * so the caller can send a single key (or several) without clobbering
   * siblings. Used by the Request Details inline-edit UI in plan-work-rework
   * PR3.
   */
  async patchSubmissionResponseData(
    slug: string,
    submissionId: string,
    partial: Record<string, unknown>,
  ): Promise<TIntakeSubmission> {
    const submission = await this.patch<TIntakeSubmission>(
      `/api/workspaces/${slug}/intake/submissions/${submissionId}/`,
      { response_data: partial },
    );
    return this.normalizeSubmission(slug, "patchSubmissionResponseData", submission);
  }

  /**
   * Paginated + search-filterable list of workspace projects the user can
   * read MINUS projects already linked to this submission via
   * ``cognota_project_submission_links``. Powers the "Link projects" modal
   * picker. Returns the standard BasePaginator envelope so the modal can
   * thread the ``next_cursor`` for load-more.
   */
  async listLinkableProjects(
    slug: string,
    submissionId: string,
    options: { search?: string; cursor?: string; pageSize?: number } = {},
    config?: ApiRequestConfig,
  ): Promise<TLinkableProjectsPage> {
    const params = new URLSearchParams();
    if (options.search) params.set("search", options.search);
    if (options.cursor) params.set("cursor", options.cursor);
    if (options.pageSize) params.set("per_page", String(options.pageSize));
    const qs = params.toString();
    return this.get(
      `/api/workspaces/${slug}/intake/submissions/${submissionId}/linkable-projects/${qs ? `?${qs}` : ""}`,
      config,
    );
  }

  /**
   * Batch-attach existing workspace projects to this submission. Idempotent
   * — already-linked ids surface in ``skipped``; only freshly-created rows
   * show up in ``linked``. Atomic: any invalid id rejects with HTTP 400
   * before a single row is written.
   */
  async linkProjectsToSubmission(
    slug: string,
    submissionId: string,
    projectIds: string[],
  ): Promise<TLinkProjectsResult> {
    return this.post(`/api/workspaces/${slug}/intake/submissions/${submissionId}/links/`, {
      project_ids: projectIds,
    });
  }

  /**
   * List every project linked to this submission, team-independent. Matches
   * both the legacy ``ProjectMetadata.intake_submission_id`` FK (set by the
   * Plan Work convert flow) and the M:N ``ProjectSubmissionLink`` rows (Link
   * Projects modal), so a freshly-created project shows up even when it has
   * no team association.
   */
  async listSubmissionLinkedProjects(
    slug: string,
    submissionId: string,
    config?: ApiRequestConfig,
  ): Promise<TLinkedProject[]> {
    return this.get(`/api/workspaces/${slug}/intake/submissions/${submissionId}/links/`, config);
  }

  /**
   * Soft-delete the ``ProjectSubmissionLink`` row between this submission
   * and ``projectId``. Idempotent — calling on an already-unlinked pair
   * still returns 204. Project itself is untouched.
   */
  async unlinkProjectFromSubmission(
    slug: string,
    submissionId: string,
    projectId: string,
  ): Promise<void> {
    return this.delete(
      `/api/workspaces/${slug}/intake/submissions/${submissionId}/links/${projectId}/`,
    );
  }

  /**
   * Soft-delete the entire intake submission (BE: BaseModel soft-delete via
   * IntakeSubmissionEndpoint.delete, gated on ``request.delete``). The
   * caller is responsible for redirecting away from the now-deleted
   * submission's URL.
   */
  async deleteSubmission(slug: string, submissionId: string): Promise<void> {
    return this.delete(`/api/workspaces/${slug}/intake/submissions/${submissionId}/`);
  }

  /** Archive (``archived: true``) or unarchive (``false``) a request. Returns
   *  the updated submission so the caller can patch it into its caches. */
  async setSubmissionArchived(
    slug: string,
    submissionId: string,
    archived: boolean,
  ): Promise<TIntakeSubmission> {
    const submission = await this.post<TIntakeSubmission>(
      `/api/workspaces/${slug}/intake/submissions/${submissionId}/archive/`,
      { archived },
    );
    return this.normalizeSubmission(slug, "setSubmissionArchived", submission);
  }

  /** Pin a request's priority tier, overriding the computed score. The tier
   *  survives every future recompute until cleared. Returns the updated
   *  submission so the caller can patch it into its caches. */
  async overrideSubmissionPriority(
    slug: string,
    submissionId: string,
    tier: TPriorityTier,
  ): Promise<TIntakeSubmission> {
    const submission = await this.post<TIntakeSubmission>(
      `/api/workspaces/${slug}/intake/submissions/${submissionId}/priority-override/`,
      { tier },
    );
    return this.normalizeSubmission(slug, "overrideSubmissionPriority", submission);
  }

  /** Clear a request's priority override so it rejoins auto-compute (the BE
   *  recomputes from the current config on clear). Returns the updated
   *  submission with the freshly-computed (or absent) priority. */
  async clearSubmissionPriorityOverride(
    slug: string,
    submissionId: string,
  ): Promise<TIntakeSubmission> {
    const submission = await this.delete<TIntakeSubmission>(
      `/api/workspaces/${slug}/intake/submissions/${submissionId}/priority-override/`,
    );
    return this.normalizeSubmission(slug, "clearSubmissionPriorityOverride", submission);
  }

  /** Fetch a request's priority score breakdown (#2634), computed on demand.
   *  `breakdown` is null when scoring is off, no config exists, or the request is
   *  manually overridden — nothing to break down. */
  async getSubmissionPriorityBreakdown(
    slug: string,
    submissionId: string,
    config?: ApiRequestConfig,
  ): Promise<{ breakdown: IPriorityBreakdown | null }> {
    return this.get(
      `/api/workspaces/${slug}/intake/submissions/${submissionId}/priority-breakdown/`,
      config,
    );
  }
}

export class IntakeSubmissionActivityService extends ApiService {
  async list(
    slug: string,
    submissionId: string,
    limit = 50,
    config?: ApiRequestConfig,
  ): Promise<TIntakeSubmissionActivity[]> {
    return this.get(`/api/workspaces/${slug}/intake/submissions/${submissionId}/activities/`, {
      ...config,
      params: { limit },
    });
  }
}

export class IntakeSubmissionCommentService extends ApiService {
  async list(
    slug: string,
    submissionId: string,
    config?: ApiRequestConfig,
  ): Promise<TIntakeSubmissionComment[]> {
    return this.get(`/api/workspaces/${slug}/intake/submissions/${submissionId}/comments/`, config);
  }

  async create(
    slug: string,
    submissionId: string,
    data: Partial<TIntakeSubmissionComment>,
  ): Promise<TIntakeSubmissionComment> {
    return this.post(`/api/workspaces/${slug}/intake/submissions/${submissionId}/comments/`, data);
  }

  async update(
    slug: string,
    submissionId: string,
    commentId: string,
    data: Partial<TIntakeSubmissionComment>,
  ): Promise<TIntakeSubmissionComment> {
    return this.patch(
      `/api/workspaces/${slug}/intake/submissions/${submissionId}/comments/${commentId}/`,
      data,
    );
  }

  async remove(slug: string, submissionId: string, commentId: string): Promise<void> {
    return this.delete(
      `/api/workspaces/${slug}/intake/submissions/${submissionId}/comments/${commentId}/`,
    );
  }
}
